import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../utils/Instance";

const ContestTable2 = ({ showViewAllButton = true }) => {
  const [selectedContest, setSelectedContest] = useState(null);
  const [contestResults, setContestResults] = useState({});
  const [loadingResults, setLoadingResults] = useState({});
  const [completedContests, setCompletedContests] = useState([]);
  const [loadingCompletedContests, setLoadingCompletedContests] = useState(false);
  const { profile } = useAuth();
  const navigate = useNavigate();

  // Fetch completed contests entered by the current user.
  useEffect(() => {
    const fetchCompletedContests = async () => {
      try {
        setLoadingCompletedContests(true);
        const response = await fetch(`${API_BASE}accounts/contests/my-results/`, {
          headers: {
            Authorization: `Bearer ${profile?.accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch completed contest results");
        }

        const data = await response.json();
        setCompletedContests(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching completed contest results:", error);
        setCompletedContests([]);
      } finally {
        setLoadingCompletedContests(false);
      }
    };

    if (profile?.accessToken) {
      fetchCompletedContests();
    }
  }, [profile?.accessToken]);

  const handleViewResults = async (contest) => {
    setSelectedContest(contest);
    setLoadingResults(prev => ({ ...prev, [contest.id]: true }));
    
    try {
      const response = await fetch(
        `${API_BASE}accounts/votes/contest_results/?contest_id=${contest.id}`,
        {
          headers: {
            'Authorization': `Bearer ${profile?.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch contest results');
      }
      
      const data = await response.json();
      setContestResults(prev => ({ ...prev, [contest.id]: data }));
    } catch (error) {
      console.error('Error fetching contest results:', error);
      setContestResults(prev => ({ ...prev, [contest.id]: { results: [] } }));
    } finally {
      setLoadingResults(prev => ({ ...prev, [contest.id]: false }));
    }
  };

  const closeModal = () => {
    setSelectedContest(null);
  };

  // Get winner for a contest
  const getContestWinner = (contest) => {
    if (contest.winner_screen_name) {
      return contest.winner_screen_name;
    }
    if (contestResults[contest.id]?.results?.[0]) {
      return contestResults[contest.id].results[0].screen_name;
    }
    return "—";
  };

  const rows = completedContests.map((contest) => ({
    ...contest,
    id: contest.id || contest.contest_id,
    title: contest.title || contest.contest_title,
    end_time: contest.end_time || contest.contest_end_time,
  }));

  return (
    <div className="w-full">
      <div className="overflow-x-auto overflow-y-auto h-[420px] rounded-lg">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#ceb46a] text-black">
              <th className="py-4 px-6 text-left">Contest</th>
              <th className="py-4 px-6 text-left">Contest Winner</th>
              <th className="py-4 px-6 text-left">Date</th>
              <th className="py-4 px-6 text-left">Result</th>
            </tr>
          </thead>
          <tbody>
            {loadingCompletedContests && (
              <tr className="bg-[#161617] text-white">
                <td colSpan="4" className="py-6 px-6 text-center text-gray-400">
                  Loading completed contests...
                </td>
              </tr>
            )}
            {!loadingCompletedContests && rows.length === 0 && (
              <tr className="bg-[#161617] text-white">
                <td colSpan="4" className="py-6 px-6 text-center text-gray-400">
                  No completed contests yet
                </td>
              </tr>
            )}
            {!loadingCompletedContests && rows.map((contest, index) => (
              <tr
                key={contest.id}
                className={
                  index % 2 === 0
                    ? "bg-[#161617] text-white"
                    : "bg-[#1C1C1E] text-white"
                }
              >
                <td className="py-4 px-6">{contest.title || contest.name}</td>
                <td className="py-4 px-6">
                  {getContestWinner(contest)}
                </td>
                <td className="py-4 px-6">
                  {contest.end_time ? new Date(contest.end_time).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) : new Date().toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </td>
                <td className="py-4 px-6">
                  <button
                    onClick={() => handleViewResults(contest)}
                    className="px-4 py-2 rounded-lg font-semibold
                               bg-[#ceb46a] text-black
                               hover:bg-[#e3c97a] 
                               transition duration-200 shadow-md
                               hover:shadow-lg active:scale-95"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {showViewAllButton && (
              <tr className="bg-[#1C1C1E]">
                <td colSpan="4" className="py-3 px-6">
                  <div className="flex justify-end">
                    <button
                      onClick={() => navigate('/all-contests-history')}
                      className="px-6 py-2 rounded-lg font-semibold
                                 bg-[#ceb46a] text-black
                                 hover:bg-[#e3c97a] 
                                 transition duration-200 shadow-md
                                 hover:shadow-lg active:scale-95"
                    >
                      View All
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {selectedContest && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-[#1C1C1E] text-white rounded-2xl shadow-lg w-3/4 max-w-3xl p-6">
            <h2 className="text-2xl font-bold text-[#ceb46a] mb-4">
              Full Results – {selectedContest.title || selectedContest.name}
            </h2>

        {loadingResults[selectedContest.id] ? (
          <div className="py-10 text-center text-gray-400">
            Loading results...
          </div>
        ) : (
          <div className="overflow-y-auto max-h-96 custom-scrollbar">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#ceb46a] text-black">
                  <th className="py-3 px-4 text-left">Place</th>
                  <th className="py-3 px-4 text-left">Screen Name</th>
                  <th className="py-3 px-4 text-left">Votes</th>
                </tr>
              </thead>
              <tbody>
                {(contestResults[selectedContest.id]?.results || []).map((result, idx) => (
                      <tr
                        key={result.participant_id}
                        className={
                          idx % 2 === 0 ? "bg-[#161617]" : "bg-[#1C1C1E]"
                        }
                      >
                    <td className="py-3 px-4 font-bold">
                      {result.place}
                    </td>
                    <td className="py-3 px-4">{result.screen_name}</td>
                    <td className="py-3 px-4">{result.total_votes}</td>
                  </tr>
                    ))}
                    {(!contestResults[selectedContest.id] || contestResults[selectedContest.id].results?.length === 0) && (
                      <tr>
                        <td colSpan="3" className="py-6 text-center text-gray-400">
                          No results available yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className="px-5 py-2 rounded-lg font-semibold
                           bg-[#ceb46a] text-black
                           hover:bg-[#e3c97a] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContestTable2;
